import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, ChevronRight, MapPin, X } from "lucide-react";

import { fetchDispatchGeotabLiveAlerts } from "../api/geotabApi.js";
import useAccess from "../hooks/useAccess.jsx";
import useDispatchDisplayPreferences from "../hooks/useDispatchDisplayPreferences.js";
import useGeotabTolerance from "../hooks/useGeotabTolerance.js";
import { ACCESS_PERMISSION_KEYS } from "../lib/accessControl.js";
import {
  ALERT_POLL_MS, MAX_ALERT_CARDS, alertAction, alertTone, alertNotificationText, alertVisible, alertsEnabled, dwellGap,
  placeLabel, takeNewAlerts,
} from "../utils/geotabLiveAlerts.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { chipClass, fmtDelta, fmtDuration, fmtTime, mapsUrl } from "./geotab/geotabFormat.js";
import "./GeotabLiveAlerts.css";

// v2 : avis reconnus par passage au lieu, sans l'heure (les anciens identifiants ne sont plus relus).
const STORAGE_KEY = "grain.geotab-live-alerts.v2";
const LiveAlertsContext = React.createContext(null);

// Avis déjà vus et lus de la journée, par utilisateur, dans ce navigateur seulement.
function readMemory(user, date) {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    if (value?.user === user && value?.date === date) return { seen: new Set(value.seen), read: new Set(value.read), firstLoad: false };
  } catch { /* stockage indisponible : on repart de zéro */ }
  return { seen: new Set(), read: new Set(), firstLoad: true };
}

function writeMemory(user, date, { seen, read }) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, date, seen: [...seen], read: [...read] }));
  } catch { /* stockage indisponible */ }
}

let audio = null;
// Petit « ding » discret, sans fichier son.
function ding() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    audio ||= new AudioContext();
    void audio.resume?.();
    const start = audio.currentTime;
    [[880, 0], [1320, 0.12]].forEach(([frequency, delay]) => {
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start + delay);
      gain.gain.exponentialRampToValueAtTime(0.16, start + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + delay + 0.35);
      oscillator.connect(gain).connect(audio.destination);
      oscillator.start(start + delay);
      oscillator.stop(start + delay + 0.4);
    });
  } catch { /* son bloqué par le navigateur */ }
}

// Avis Windows quand le SaaS n'est pas la fenêtre active ; même étiquette : un seul avis si plusieurs onglets sont ouverts.
// « Fixe » : Chrome le garde jusqu'à ce qu'on le ferme ; sinon Windows décide de sa durée.
function notifyDesktop(events, persistent) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted" || document.hasFocus()) return;
  events.slice(0, 3).forEach((event) => {
    const { title, body } = alertNotificationText(event);
    try {
      new Notification(title, { body, tag: event.id, requireInteraction: persistent });
    } catch { /* avis refusé par le système */ }
  });
}

// Arrêt hors plan : depuis combien de temps (à l'arrivée) ou combien de temps (au départ), puis l'arrêt prévu suivant.
function OffPlanMeta({ event }) {
  return (
    <>
      <div>{event.type === "arrival" ? <>Arrêté depuis <b>{fmtDuration(event.elapsed)}</b></> : <><b>{fmtDuration(event.dwell)}</b> hors plan</>} · pas dans le Dispatch</div>
      <div className="geotab-alert-next" title={event.next || "retour au dépôt"}>{event.type === "arrival" ? "Prochain prévu" : "Suivant"} : <b>{event.next || "retour au dépôt"}</b></div>
    </>
  );
}

function AlertCard({ event, seconds, tolerance, onDismiss }) {
  const arrival = event.type === "arrival";
  const off = event.site_kind === "off";
  const gap = dwellGap(event, tolerance);
  const mapLink = off && Number.isFinite(event.lat) && Number.isFinite(event.lon) ? mapsUrl(event.lat, event.lon) : null;
  return (
    <div className={`geotab-alert-card tone-${alertTone(event)}`} data-geotab-alert={event.type} data-geotab-alert-tone={alertTone(event)}>
      <span className="geotab-alert-icon" aria-hidden="true">{arrival ? (off ? <AlertTriangle /> : <MapPin />) : <Check />}</span>
      <div className="min-w-0">
        <div className="geotab-alert-head">
          <span className="geotab-alert-driver">{event.driver || "Chauffeur à compléter"}</span>
          {event.truck ? <span className="geotab-alert-truck">{event.truck}</span> : null}
          <span className="geotab-alert-time">{fmtTime(event.at)}</span>
        </div>
        <div className="geotab-alert-place">
          <div className="geotab-alert-what" title={event.site}>{alertAction(event)} <b>{event.site}</b></div>
          {mapLink ? (
            <a className="geotab-alert-map" href={mapLink} target="_blank" rel="noopener noreferrer"
              title="Ouvrir cet arrêt dans Google Maps" aria-label="Ouvrir cet arrêt dans Google Maps (nouvel onglet)">
              <MapPin aria-hidden="true" />
            </a>
          ) : null}
        </div>
        <div className="geotab-alert-meta">
          {off ? <OffPlanMeta event={event} /> : arrival ? (
            <>
              <div>{placeLabel(event)} · arrêt <b>{event.stop}</b> sur {event.stops}{event.plan_dwell != null ? <> · prévu <b>{fmtDuration(event.plan_dwell)}</b> sur place</> : null}</div>
              {event.plan_dwell != null ? <div>Devrait repartir vers <b>{fmtTime(event.at + event.plan_dwell)}</b></div> : null}
            </>
          ) : (
            <>
              <div>
                <b>{fmtDuration(event.dwell)}</b> sur place
                {gap ? <span className={`geotab-alert-chip ${chipClass(gap.tone)}`}>{fmtDelta(gap.gap)}</span> : " · "}
                {event.plan_dwell != null ? `prévu ${fmtDuration(event.plan_dwell)}` : null}
              </div>
              <div className="geotab-alert-next" title={event.next}>{event.next ? <>Suivant : <b>{event.next}</b></> : "Dernier arrêt prévu"}</div>
            </>
          )}
        </div>
      </div>
      <button type="button" className="geotab-alert-close" aria-label="Fermer l'avis" onClick={() => onDismiss(event.id)}><X /></button>
      {seconds ? <span className="geotab-alert-bar" style={{ "--geotab-alert-ms": `${seconds * 1000}ms` }} onAnimationEnd={() => onDismiss(event.id)} /> : null}
    </div>
  );
}

// Cartes sous la cloche : deux au plus, les autres attendent derrière « N autres avis ».
function AlertCards({ cards, seconds, tolerance, onDismiss, onOpenJournal }) {
  const [anchor, setAnchor] = React.useState({ top: 44, right: 12 });
  const hasCards = cards.length > 0;
  React.useLayoutEffect(() => {
    if (!hasCards) return undefined;
    const place = () => {
      const bell = [...document.querySelectorAll('[data-notification-center="true"]')]
        .map((element) => element.getBoundingClientRect()).find((rect) => rect.width > 0);
      if (bell) {
        const viewportWidth = document.documentElement.clientWidth;
        const cardWidth = Math.min(384, viewportWidth - 16);
        const right = Math.min(Math.max(8, viewportWidth - bell.right), viewportWidth - cardWidth - 8);
        setAnchor({ top: bell.bottom + 8, right });
      }
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [hasCards]);
  if (!hasCards) return null;
  const hidden = cards.slice(MAX_ALERT_CARDS);
  const stack = (
    <div className="geotab-alert-stack" style={anchor} aria-live="polite" data-geotab-alert-stack="true">
      {cards.slice(0, MAX_ALERT_CARDS).map((event) => <AlertCard key={event.id} event={event} seconds={seconds} tolerance={tolerance} onDismiss={onDismiss} />)}
      {hidden.length ? (
        <button type="button" className="geotab-alert-more" onClick={onOpenJournal}>
          <span className={`geotab-alert-dot tone-${alertTone(hidden[0])}`} aria-hidden="true" />
          <span className="min-w-0 truncate"><b>{hidden.length === 1 ? "1 autre avis" : `${hidden.length} autres avis`}</b> · {fmtTime(hidden[0].at)} · {hidden[0].driver}</span>
          <span className="geotab-alert-more-go">Tout voir <ChevronRight className="h-3.5 w-3.5" /></span>
        </button>
      ) : null}
    </div>
  );
  const root = getReactPortalRoot();
  return root instanceof HTMLElement ? createPortal(stack, root) : stack;
}

export function GeotabLiveAlertsProvider({ children }) {
  const { access, hasPermission, isReady } = useAccess();
  const canUse = isReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchGeotabRealRead);
  const [display] = useDispatchDisplayPreferences({ enabled: canUse });
  const tolerance = useGeotabTolerance(canUse);
  const enabled = canUse && alertsEnabled(display);
  const user = access?.userId || "";
  const [payload, setPayload] = React.useState(null);
  const [error, setError] = React.useState("");
  const [cards, setCards] = React.useState([]);
  const [readVersion, setReadVersion] = React.useState(0);
  const [journalRequest, setJournalRequest] = React.useState(0);
  const memory = React.useRef(null);
  const displayRef = React.useRef(display);
  displayRef.current = display;

  const load = React.useCallback(async () => {
    let data;
    try {
      data = await fetchDispatchGeotabLiveAlerts();
    } catch (failure) {
      setError(failure?.message || "Geotab est indisponible.");
      return;
    }
    setError("");
    setPayload(data);
    if (!data?.date) return;
    if (memory.current?.key !== `${user}|${data.date}`) memory.current = { key: `${user}|${data.date}`, ...readMemory(user, data.date) };
    const fresh = takeNewAlerts(data.events || [], { ...memory.current, display: displayRef.current, now: data.now });
    memory.current.firstLoad = false;
    writeMemory(user, data.date, memory.current);
    setReadVersion((version) => version + 1);
    if (!fresh.length) return;
    setCards((current) => [...fresh, ...current].slice(0, 10));
    if (displayRef.current.alert_sound) ding();
    if (displayRef.current.alert_desktop) notifyDesktop(fresh, displayRef.current.alert_seconds === 0);
  }, [user]);

  React.useEffect(() => {
    if (!enabled) {
      setCards([]);
      return undefined;
    }
    // Onglet caché : on continue seulement pour les avis Windows.
    const tick = () => {
      if (displayRef.current.alert_desktop || document.visibilityState === "visible") void load();
    };
    tick();
    const timer = window.setInterval(tick, ALERT_POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, load]);

  const events = React.useMemo(
    () => (payload?.events || []).filter((event) => alertVisible(event, display)),
    [payload, display],
  );
  const unreadIds = React.useMemo(() => {
    const read = memory.current?.read;
    return new Set(events.filter((event) => !read?.has(event.id)).map((event) => event.id));
    // readVersion : la mémoire des avis lus change sans nouvel état React.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, readVersion]);
  const markAllRead = React.useCallback(() => {
    const current = memory.current;
    if (!current || !events.some((event) => !current.read.has(event.id))) return;
    events.forEach((event) => current.read.add(event.id));
    writeMemory(user, payload?.date, current);
    setReadVersion((version) => version + 1);
  }, [events, payload?.date, user]);
  const dismiss = React.useCallback((id) => setCards((current) => current.filter((event) => event.id !== id)), []);
  // Clic sur un avis de la cloche : on réaffiche sa carte en tête de pile.
  const showCard = React.useCallback((event) => setCards((current) => [event, ...current.filter((card) => card.id !== event.id)]), []);
  const openJournal = React.useCallback(() => {
    setCards([]);
    setJournalRequest((request) => request + 1);
  }, []);
  // Liste des chauffeurs du menu, même avant d'activer les avis.
  const refreshDrivers = React.useCallback(() => { if (canUse) void load(); }, [canUse, load]);

  const value = React.useMemo(() => ({
    canUse, enabled, tolerance, date: payload?.date || "", events, drivers: payload?.drivers || [], error, unreadIds,
    unreadCount: enabled ? unreadIds.size : 0, markAllRead, journalRequest, openJournal, showCard, refreshDrivers,
  }), [canUse, enabled, tolerance, payload, events, error, unreadIds, markAllRead, journalRequest, openJournal, showCard, refreshDrivers]);

  return (
    <LiveAlertsContext.Provider value={value}>
      {children}
      {enabled ? <AlertCards cards={cards} seconds={display.alert_seconds ?? 20} tolerance={tolerance} onDismiss={dismiss} onOpenJournal={openJournal} /> : null}
    </LiveAlertsContext.Provider>
  );
}

export function useGeotabLiveAlerts() {
  return React.useContext(LiveAlertsContext);
}
